import { create } from 'zustand';
import type { CustomerDocType, ServiceDocType, PackageDocType, PriceTier } from '../db/schemas';
import { generateId } from '../lib/uuid';

export type POSStep = 'cart' | 'tips' | 'payment' | 'summary';

export interface POSCartItem {
  id: string; // local UI id
  type?: 'service' | 'package';
  service?: ServiceDocType;
  package?: PackageDocType;
  selectedTier?: PriceTier;
  operatorId: string | null;
  unitPrice: number;
  discountAmount?: number;
  quantity: number;
}

export interface POSPaymentEntry {
  id: string;
  paymentMethodId: string;
  amount: number;
  reference?: string;
}

interface POSState {
  isOpen: boolean;
  isMinimized: boolean;
  step: POSStep;
  isSelectingService: boolean;
  isCustomerSearchOpen: boolean;
  customer: CustomerDocType | null;
  defaultOperatorId: string | null;
  cartItems: POSCartItem[];
  paymentEntries: POSPaymentEntry[];
  operationalDate: string;
  
  // Modifiers & Debt/Change controls
  discount: number;
  tip: number;
  tipPercentage: number | null;
  tipOperatorId: string | null;
  includePreviousDebt: boolean;
  surplusMode: 'change' | 'credit';
  
  // Contextual staff assignment
  pendingStaffTargetItemId: string | 'default' | null;
  pendingServiceSelection: {
    type: 'service' | 'package';
    service?: ServiceDocType;
    package?: PackageDocType;
    tier?: PriceTier | null;
  } | null;
  isStaffFormOpen: boolean;
  editingCartItemId: string | null;

  // Actions
  open: (customer?: CustomerDocType | null) => void;
  close: (force?: boolean) => void;
  minimize: () => void;
  resume: () => void;
  discardDraft: () => void;
  setPendingStaffTargetItemId: (target: string | 'default' | null) => void;
  setEditingCartItemId: (id: string | null) => void;
  openStaffCreation: (
    targetItemId?: string | 'default' | null,
    pendingSelection?: {
      type: 'service' | 'package';
      service?: ServiceDocType;
      package?: PackageDocType;
      tier?: PriceTier | null;
    } | null
  ) => void;
  closeStaffCreation: () => void;
  onStaffCreated: (newStaff: { id: string; name: string }) => void;
  setStep: (step: POSStep) => void;
  setIsSelectingService: (isSelecting: boolean) => void;
  setIsCustomerSearchOpen: (isOpen: boolean) => void;
  setCustomer: (customer: CustomerDocType | null) => void;
  setDefaultOperatorId: (operatorId: string | null) => void;
  setOperationalDate: (date: string) => void;
  setIncludePreviousDebt: (include: boolean) => void;
  setSurplusMode: (mode: 'change' | 'credit') => void;
  
  setDiscount: (amount: number) => void;
  setTip: (amount: number, percentage?: number | null, operatorId?: string | null) => void;
  
  // Cart Actions
  addServiceToCart: (service: ServiceDocType, operatorId?: string | null, tier?: PriceTier | null) => void;
  addPackageToCart: (pkg: PackageDocType, operatorId?: string | null) => void;
  updateCartItem: (itemId: string, updates: Partial<POSCartItem>) => void;
  removeCartItem: (itemId: string) => void;
  clearCart: () => void;
  
  // Payment Actions
  addPaymentEntry: (entry: { paymentMethodId: string; amount: number; reference?: string }) => void;
  updatePaymentEntry: (entryId: string, updates: Partial<POSPaymentEntry>) => void;
  removePaymentEntry: (entryId: string) => void;
  clearPayments: () => void;

  // Reset entirely
  reset: () => void;
}

const initialState = {
  isOpen: false,
  isMinimized: false,
  isStaffFormOpen: false,
  step: 'cart' as POSStep,
  isSelectingService: false,
  isCustomerSearchOpen: false,
  customer: null,
  defaultOperatorId: null,
  cartItems: [] as POSCartItem[],
  paymentEntries: [] as POSPaymentEntry[],
  discount: 0,
  tip: 0,
  tipPercentage: null,
  tipOperatorId: null,
  operationalDate: new Date().toISOString(),
  includePreviousDebt: false,
  surplusMode: 'change' as const,
  pendingStaffTargetItemId: null as string | 'default' | null,
  pendingServiceSelection: null as {
    type: 'service' | 'package';
    service?: ServiceDocType;
    package?: PackageDocType;
    tier?: PriceTier | null;
  } | null,
  editingCartItemId: null as string | null,
};

export const usePOSStore = create<POSState>((set) => ({
  ...initialState,

  open: (customer = null) => set((state) => {
    const hasCartItems = state.cartItems.length > 0;
    const hasDraftCustomer = Boolean(state.customer);
    const isSameCustomer = Boolean(customer && state.customer && customer.id === state.customer.id);
    const isNoCustomerResuming = !customer && (hasCartItems || hasDraftCustomer);

    if (isNoCustomerResuming || (isSameCustomer && hasCartItems)) {
      return {
        isOpen: true,
        isMinimized: false,
      };
    }

    return {
      isOpen: true,
      isMinimized: false,
      isStaffFormOpen: false,
      step: 'cart',
      customer: customer,
      defaultOperatorId: null,
      cartItems: [],
      paymentEntries: [],
      discount: 0,
      tip: 0,
      tipPercentage: null,
      tipOperatorId: null,
      operationalDate: new Date().toISOString(),
      includePreviousDebt: false,
      surplusMode: 'change',
      pendingStaffTargetItemId: null,
      pendingServiceSelection: null,
    };
  }),
  
  close: (force = false) => set((state) => {
    const hasDraft = state.cartItems.length > 0 || Boolean(state.customer);
    if (!force && hasDraft) {
      return {
        isOpen: false,
        isMinimized: true,
        isSelectingService: false,
      };
    }
    return {
      isOpen: false,
      isMinimized: false,
      isSelectingService: false,
    };
  }),

  minimize: () => set({
    isOpen: false,
    isMinimized: true,
    isSelectingService: false,
  }),

  resume: () => set({
    isOpen: true,
    isMinimized: false,
    isStaffFormOpen: false,
  }),

  openStaffCreation: (targetItemId = 'default', pendingSelection = null) => set({
    isStaffFormOpen: true,
    isOpen: false,
    isMinimized: true,
    pendingStaffTargetItemId: targetItemId,
    pendingServiceSelection: pendingSelection,
  }),

  closeStaffCreation: () => set({
    isStaffFormOpen: false,
    isOpen: true,
    isMinimized: false,
  }),

  onStaffCreated: (newStaff) => set((state) => {
    let updatedCartItems = [...state.cartItems];

    // If there was a pending service selection from POSServiceSelector, add it to cart immediately
    if (state.pendingServiceSelection) {
      const { type, service, package: pkg, tier } = state.pendingServiceSelection;
      if (type === 'service' && service) {
        const price = tier ? (tier.total_price ?? 0) : (service.currency_price || 0);
        updatedCartItems.push({
          id: generateId('cart_srv'),
          type: 'service',
          service,
          selectedTier: tier || undefined,
          operatorId: newStaff.id,
          unitPrice: price,
          quantity: 1,
        });
      } else if (type === 'package' && pkg) {
        updatedCartItems.push({
          id: generateId('cart_pkg'),
          type: 'package',
          package: pkg,
          operatorId: newStaff.id,
          unitPrice: pkg.currency_price || 0,
          quantity: 1,
        });
      }
    }

    // If an existing cart item was being edited, assign this operator to it
    if (state.pendingStaffTargetItemId && state.pendingStaffTargetItemId !== 'default') {
      updatedCartItems = updatedCartItems.map((item) =>
        item.id === state.pendingStaffTargetItemId ? { ...item, operatorId: newStaff.id } : item
      );
    }

    // Auto-assign to any items that were previously unassigned
    updatedCartItems = updatedCartItems.map((item) =>
      !item.operatorId ? { ...item, operatorId: newStaff.id } : item
    );

    return {
      isStaffFormOpen: false,
      isOpen: true,
      isMinimized: false,
      isSelectingService: false,
      defaultOperatorId: newStaff.id,
      cartItems: updatedCartItems,
      pendingStaffTargetItemId: null,
      pendingServiceSelection: null,
    };
  }),

  discardDraft: () => set({
    ...initialState,
    operationalDate: new Date().toISOString(),
  }),

  setPendingStaffTargetItemId: (target) => set({ pendingStaffTargetItemId: target }),

  setEditingCartItemId: (editingCartItemId) => set({ editingCartItemId }),
  
  setStep: (step) => set({ step }),
  
  setIsSelectingService: (isSelectingService) => set({ isSelectingService }),
  
  setIsCustomerSearchOpen: (isCustomerSearchOpen) => set({ isCustomerSearchOpen }),
  
  setCustomer: (customer) => set({ customer, includePreviousDebt: false }),

  setDefaultOperatorId: (operatorId) => set({ defaultOperatorId: operatorId }),

  setOperationalDate: (date) => set({ operationalDate: date }),

  setIncludePreviousDebt: (includePreviousDebt) => set({ includePreviousDebt }),

  setSurplusMode: (surplusMode) => set({ surplusMode }),

  setDiscount: (amount) => set({ discount: amount }),

  setTip: (amount, percentage = null, operatorId = null) => set({
    tip: amount,
    tipPercentage: percentage,
    tipOperatorId: operatorId,
  }),

  addServiceToCart: (service, operatorId = null, tier = null) => set((state) => {
    const price = tier ? (tier.total_price ?? 0) : (service.currency_price || 0);
    const assignedOperatorId = operatorId || state.defaultOperatorId;
    const newItem: POSCartItem = {
      id: generateId('cart_srv'),
      type: 'service',
      service,
      selectedTier: tier || undefined,
      operatorId: assignedOperatorId,
      unitPrice: price,
      quantity: 1,
    };
    return { cartItems: [...state.cartItems, newItem] };
  }),

  addPackageToCart: (pkg, operatorId = null) => set((state) => {
    const assignedOperatorId = operatorId || state.defaultOperatorId;
    const newItem: POSCartItem = {
      id: generateId('cart_pkg'),
      type: 'package',
      package: pkg,
      operatorId: assignedOperatorId,
      unitPrice: pkg.currency_price || 0,
      quantity: 1,
    };
    return { cartItems: [...state.cartItems, newItem] };
  }),

  updateCartItem: (itemId, updates) => set((state) => ({
    cartItems: state.cartItems.map((item) => {
      if (item.id !== itemId) return item;
      return { ...item, ...updates };
    })
  })),

  removeCartItem: (itemId) => set((state) => ({
    cartItems: state.cartItems.filter((item) => item.id !== itemId)
  })),

  clearCart: () => set({ cartItems: [], defaultOperatorId: null }),

  addPaymentEntry: (entry) => set((state) => {
    const newEntry: POSPaymentEntry = {
      id: generateId('pay'),
      paymentMethodId: entry.paymentMethodId || '',
      amount: entry.amount || 0,
      reference: entry.reference,
    };
    return { paymentEntries: [...state.paymentEntries, newEntry] };
  }),

  updatePaymentEntry: (entryId: string, updates: Partial<POSPaymentEntry>) => set((state) => ({
    paymentEntries: state.paymentEntries.map((item) => {
      if (item.id !== entryId) return item;
      return { ...item, ...updates };
    })
  })),

  removePaymentEntry: (entryId) => set((state) => ({
    paymentEntries: state.paymentEntries.filter((item) => item.id !== entryId)
  })),

  clearPayments: () => set({ paymentEntries: [] }),

  reset: () => set({ ...initialState, operationalDate: new Date().toISOString() }),
}));
